import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { APP_PATHS } from '@dev-fast/types';
import { type NgxsOnInit, type StateContext, Action, Selector, State } from '@ngxs/store';
import type { Observable } from 'rxjs';
import { filter, tap } from 'rxjs';

import { LanguageService } from '@app/core/language-service';

import { ClearHistory, NavigateBack, PopHistory, PushHistory } from './navigation.actions';
import { type INavigationStateModel, NAVIGATION_INITIAL_STATE } from './navigation.model';

@State<INavigationStateModel>({
  defaults: NAVIGATION_INITIAL_STATE,
  name: 'navigation',
})
@Injectable()
export class NavigationState implements NgxsOnInit {
  // Подключение сервисов
  readonly #router = inject(Router);

  // Селекторы
  @Selector()
  static history({ history }: INavigationStateModel): string[] {
    return history;
  }

  // Методы жизненного цикла
  ngxsOnInit(stateContext: StateContext<INavigationStateModel>): void {
    const { dispatch } = stateContext;

    let isBackNavigation = false;

    // Отслеживаем начало навигации (чтобы сбросить флаг "назад")
    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        tap((event) => {
          // Если это программная навигация - сбрасываем флаг браузерной навигации
          if (event.navigationTrigger === 'imperative') {
            isBackNavigation = false;
          }
          // Отслеживаем "Назад"/"Вперед" в браузере
          if (event.navigationTrigger === 'popstate') {
            isBackNavigation = true;
          }
        }),
      )
      .subscribe();

    // Отслеживаем конец навигации и добавляем в историю, если это не "Назад"
    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        // фильтровать, если url модального окна
        filter((event) => !this.#isModalUrl(event.urlAfterRedirects)),
        // фильтровать, если url совпадает с последним в истории
        filter((event) => {
          const baseUrl = LanguageService.getBaseUrl(event.urlAfterRedirects);
          return !this.#isLastUrl(stateContext, baseUrl);
        }),
        // НЕ добавляем в историю, если был переход "Назад"
        filter(() => !isBackNavigation),
        tap((event) => {
          const baseUrl = LanguageService.getBaseUrl(event.urlAfterRedirects);
          dispatch(new PushHistory(baseUrl));
        }),
        // Сбрасываем флаг после обработки
        tap(() => {
          isBackNavigation = false;
        }),
      )
      .subscribe();
  }

  /**
   * Возвращает на предыдущий урл. Если мы находимся на странице кейса,
   *  а предыдущий урл ведет не на кейсы,
   * вернёт на главную кейсов.
   */
  @Action(NavigateBack)
  navigateBack(
    { dispatch, getState }: StateContext<INavigationStateModel>,
    { fromCasesPage = false, initialUrl = APP_PATHS.slash }: NavigateBack,
  ): Observable<void> {
    return dispatch(new PopHistory()).pipe(
      tap(() => {
        const { history } = getState();
        const lastHistoryUrl = history.length ? history[history.length - 1] : initialUrl;
        if (fromCasesPage) {
          const backUrl = lastHistoryUrl.includes(`/${APP_PATHS.games.vaults.base}/${APP_PATHS.games.vaults.vault}/`)
            ? lastHistoryUrl
            : APP_PATHS.games.vaults.base;
          return this.#router.navigateByUrl(backUrl);
        }

        return this.#router.navigateByUrl(lastHistoryUrl);
      }),
    );
  }

  @Action(PopHistory)
  popHistory({ getState, patchState }: StateContext<INavigationStateModel>): void {
    const { history } = getState();

    patchState({ history: history.slice(0, -1) });
  }

  @Action(PushHistory)
  pushHistory({ getState, patchState }: StateContext<INavigationStateModel>, { newUrl }: PushHistory): void {
    const { history } = getState();

    patchState({ history: history.concat(newUrl) });
  }

  @Action(ClearHistory)
  clearHistory({ patchState }: StateContext<INavigationStateModel>): void {
    patchState({ history: [] });
  }

  // Прочие методы
  #isLastUrl({ getState }: StateContext<INavigationStateModel>, url: string): boolean {
    const { history } = getState();

    return !!history.length && history[history.length - 1] === url;
  }

  #isModalUrl(url: string): boolean {
    return url.includes('modal:');
  }
}
